<script setup lang="ts">
import {ref, watch} from "vue";
import {Button} from "@/core/components/ui/button";
import {ScanSearch} from "@lucide/vue";
import useLiveActivation from "@/live/composables/useLiveActivation";
import LiveActivationDialog from "@/live/components/LiveActivationDialog.vue";
import LiveAuthorizationYes from "@/live/components/LiveAuthorizationYes.vue";

/**
 * LIVE — the activation of the automatic patient detection (AP-7768), worded
 * as an update of Apodis: a dialog the first time it is offered after a shell
 * update, then a card on the home until the pharmacist accepts it or chooses
 * "Plus tard".
 */
const {isVisible, canPrompt, claimDialog, snooze, activating, failed, activate} = useLiveActivation();

const dialogOpen = ref(false);

watch(canPrompt, (ready) => {
    if (ready && claimDialog()) {
        dialogOpen.value = true;
    }
}, {immediate: true});

const onActivate = async () => {
    const result = await activate();

    if (result.status !== 'failed') {
        dialogOpen.value = false;
    }
};

const onLater = () => {
    snooze();
    dialogOpen.value = false;
};
</script>

<template>
  <div
    v-if="isVisible"
    class="flex flex-row items-center gap-4 p-4 bg-white rounded-lg border-2 border-primary-lightest"
  >
    <div class="flex items-center justify-center shrink-0 size-12 rounded-2xl bg-lightPurple-lightest">
      <ScanSearch class="size-6 text-primary" />
    </div>
    <div class="flex flex-col gap-1 flex-1">
      <h2 class="text-black">
        Mise à jour d'Apodis
      </h2>
      <p>
        Une mise à jour est disponible pour assurer le bon fonctionnement d'Apodis.
      </p>
      <p class="font-semibold text-black">
        Une demande d'autorisation Windows va apparaître. Cliquez sur <LiveAuthorizationYes /> pour poursuivre.
      </p>
      <p
        v-if="failed"
        class="text-sm text-red-light"
      >
        La mise à jour n'a pas abouti. Réessayez, ou contactez le support Apodis.
      </p>
    </div>
    <div class="flex flex-row items-center gap-2 shrink-0">
      <Button
        variant="ghost"
        class="rounded-full"
        :disabled="activating"
        @click="onLater"
      >
        Plus tard
      </Button>
      <Button
        class="bg-primary rounded-full"
        :disabled="activating"
        @click="onActivate"
      >
        Mettre à jour
      </Button>
    </div>

    <LiveActivationDialog
      v-model:open="dialogOpen"
      :activating="activating"
      :failed="failed"
      @activate="onActivate"
      @later="onLater"
    />
  </div>
</template>
